import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ShieldAlert } from 'lucide-react';
import { adminImpersonateStop } from '@/api/admin';
import { announceIdentityChanged, resyncIdentity, useAuthStore } from '@/store/authStore';
import { useToast } from '@/components/ui/use-toast';

/**
 * "You are in Test Mode" — the band across the top of every screen while an admin is inside
 * somebody else's account, and the way back out.
 *
 * Until this existed there was no way to tell. Impersonation looks exactly like being signed in
 * as that agent: their sites, their brand, their listings, their name in the header. The only
 * difference is that anything typed lands in a real customer's account. `adminImpersonateStop` had
 * been in the API client for months with no caller at all, so the only exit was clearing cookies
 * by hand or waiting out the hour.
 *
 * Deliberately a BAND, not a toast or a badge: it has to survive navigation, be impossible to
 * dismiss, and take space rather than float over content — someone who forgets they are in test
 * mode is the whole hazard, and a notice they can lose is no notice.
 */
/** py-2 + text-sm line-height + the border. Kept beside the class that produces it. */
const BAR_HEIGHT = '2.25rem';

export default function ImpersonationRibbon(): JSX.Element | null {
  const impersonating = useAuthStore((s) => s.impersonating);
  const email = useAuthStore((s) => s.email);
  // A managed site (lib/limitedAccess.ts): staff aren't limited in Test Mode, the client is.
  const siteManaged = useAuthStore((s) => s.limitedAccess.site_managed);
  const navigate = useNavigate();
  const { toast } = useToast();
  const [leaving, setLeaving] = useState(false);

  /**
   * Publish the band's height so the app's full-height layouts can subtract it.
   *
   * The Studio is `h-[calc(100vh-3.5rem)]` and the Blog workspace `calc(100vh-180px)` — both
   * assume nothing sits above the header. Without this the band pushes each of them past the
   * fold and the page grows a scrollbar, in exactly the mode someone is most likely to think
   * the app is broken. Zero is the default in index.css, so an ordinary session is untouched,
   * and the effect clears on unmount (leaving impersonation) rather than leaving a gap behind.
   */
  useEffect(() => {
    const root = document.documentElement;
    if (!impersonating) return;
    root.style.setProperty('--impersonation-bar', BAR_HEIGHT);
    return () => { root.style.removeProperty('--impersonation-bar'); };
  }, [impersonating]);

  if (!impersonating) return null;

  async function returnToAdmin() {
    if (leaving) return;
    setLeaving(true);
    // The SERVER decides whether Test Mode is over — not the stop call's success, and not this
    // tab's belief. After asking it there are three outcomes:
    //   • confirmed back as the admin → in-app navigation to Accounts;
    //   • still in Test Mode → say so and stay, band up;
    //   • no confirmation at all (the re-read failed, or did not answer within its bound) → a FULL
    //     RELOAD, which starts from the cookies instead of this tab's possibly stale identity.
    // A stop that failed WITHOUT a clear answer (a timeout, a 5xx) is not taken as "still in Test
    // Mode" either: the impersonation may have ended anyway, and a band left up over the admin's
    // session would put the admin's data under the agent's name. The re-read settles it.
    //
    // No explicit clear of the wizard's progress here: it belongs to the account being left and is
    // cleared when leaving is CONFIRMED (hydrate resets account state on the identity change; a
    // full reload finds the saved owner differs).
    let stopFailure: unknown = null;
    try {
      // Clears ONLY the impersonation cookies — the admin's own session is untouched.
      await adminImpersonateStop();
    } catch (e) {
      // The stop came back on a LAPSED cookie and the re-read behind it could not confirm the
      // admin: the cookies are already gone, so staying in Test Mode would be the stale state.
      if ((e as { serverCode?: string } | null)?.serverCode === 'impersonation_ended_unsynced') {
        window.location.assign('/root/users');
        return;
      }
      stopFailure = e;
    }
    const back = await resyncIdentity();
    // Every other tab: announce, not set — `impersonating` stays the server's answer.
    announceIdentityChanged();
    if (!back) {
      window.location.assign('/root/users');
      return;
    }
    if (useAuthStore.getState().impersonating) {
      // Staying put is the safe failure: still impersonating, still saying so.
      toast({
        title: 'Could not leave test mode',
        description: stopFailure instanceof Error
          ? stopFailure.message
          : 'The server still reports Test Mode for this session. Please try again.',
        variant: 'destructive',
      });
      setLeaving(false);
      return;
    }
    navigate('/root/users', { replace: true });
  }
  return (
    // `sticky`, not `fixed`: the band takes its own space at the top of the document instead of
    // floating over the app's header, so nothing underneath is covered and no layout needs a
    // matching offset. z-50 keeps it above the Studio's own sticky chrome.
    <div
      role="status"
      className="sticky top-0 z-50 w-full bg-brand-error text-white shadow-md print:hidden"
    >
      <button
        type="button"
        onClick={returnToAdmin}
        disabled={leaving}
        style={{ height: BAR_HEIGHT }}
        className="flex w-full items-center justify-center gap-2 px-4 text-center text-sm font-semibold tracking-wide transition-colors hover:bg-[#A93226] focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-brand-error disabled:opacity-80"
      >
        {leaving ? (
          <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
        ) : (
          <ShieldAlert className="h-4 w-4 shrink-0" aria-hidden />
        )}
        {/* `truncate` + `min-w-0`, because the height is FIXED and published as
            `--impersonation-bar` for every shell to subtract. Text that wraps inside a fixed
            height does not make the bar taller — it spills out of it, and the shells go on
            reserving the old height. An address like a long agent email on a phone is enough to
            do it. Truncating keeps the rendered height and the published constant the same fact. */}
        <span className="min-w-0 truncate">
          You are in Test Mode{email ? <span className="font-normal"> as {email}</span> : null}
          {siteManaged ? <span className="font-normal" data-managed-note> · Managed site: the client sees a limited view</span> : null} —{' '}
          <span className="underline underline-offset-2">
            {leaving ? 'Returning to Admin…' : 'Click Here to Return to Admin'}
          </span>
        </span>
      </button>
    </div>
  );
}
